import { call, put, takeLatest, select } from "redux-saga/effects";
import { 
  setRecentSearches, 
  setRecentSearchesLoading, 
  setRecentSearchesError,
  addRecentSearch,
  loadLocalRecentSearches 
} from "./recentSearchesSlice";
import userProfileService from "../../services/userProfileService";
import { trackSearch } from "../../utils/activityTracking";

/**
 * Handle loading recent searches from backend or localStorage
 */
function* handleLoadRecentSearches() {
  try {
    // Get current user state
    const userState = yield select(state => state.user);
    
    if (!userState?.user?.id) {
      // For non-authenticated users, load from localStorage
      const localSearches = loadLocalRecentSearches();
      yield put(setRecentSearches(localSearches));
      console.log('Loaded recent searches from localStorage for non-authenticated user');
    } else {
      // For authenticated users, recent searches are loaded via prefsSaga when profile is fetched
      console.log('Recent searches will be loaded via profile API');
    }

  } catch (error) {
    console.error('Failed to load recent searches:', error);
    yield put(setRecentSearchesError(error.message));
  }
}

/**
 * Save to localStorage for non-authenticated users
 */
function saveToLocalStorage(searchData) {
  try {
    const existingData = localStorage.getItem('recentSearchesV2');
    let recentSearches = { recent: [] };

    if (existingData) {
      recentSearches = JSON.parse(existingData);
    }

    // Create search entry
    const searchEntry = {
      searchKey: searchData.searchKey,
      params: searchData.params,
      savedOffers: searchData.savedOffers || [],
      filters: searchData.filters || {},
      timestamp: new Date().toISOString()
    };

    // Remove duplicate if exists
    recentSearches.recent = recentSearches.recent.filter(
      search => search.searchKey !== searchData.searchKey
    );

    // Add new search at the beginning
    recentSearches.recent.unshift(searchEntry);

    // Keep only last 10 searches
    recentSearches.recent = recentSearches.recent.slice(0, 10);

    // Save back to localStorage
    localStorage.setItem('recentSearchesV2', JSON.stringify(recentSearches));
    
    return recentSearches.recent;
  } catch (error) {
    console.error('Failed to save to localStorage:', error);
    return null;
  }
}

/**
 * Handle saving a new recent search
 */
function* handleSaveRecentSearch(action) {
  try {
    const searchData = action.payload;
    const userState = yield select(state => state.user);
    
    if (!searchData) {
      return;
    }

    if (userState?.user?.id) {
      // Authenticated user - save to backend via activity tracking
      const updatedSearches = yield call(trackSearch, searchData);
      
      if (updatedSearches) {
        // Update Redux store with the updated list from backend
        yield put(setRecentSearches(updatedSearches));
      }
    } else {
      // Non-authenticated user - save to backend via activity tracking
      yield call(trackSearch, searchData);
      
      // Also save to localStorage for immediate UI update
      const updatedSearches = saveToLocalStorage(searchData);
      
      if (updatedSearches) {
        // Update Redux store with the updated list from localStorage
        yield put(setRecentSearches(updatedSearches));
      }
    }

  } catch (error) {
    console.error('Failed to save recent search:', error);
    // Don't update error state for save failures to avoid disrupting UX
  }
}

/**
 * Handle adding a recent search (triggered when slice action is dispatched)
 * This will save the current state to localStorage after the Redux state is updated
 */
function* handleAddRecentSearch(action) {
  try {
    // Get the current state after the action has been processed
    const state = yield select();
    const recentSearches = state.recentSearches?.recent || [];
    
    // Save to localStorage
    const dataToSave = { recent: recentSearches };
    localStorage.setItem('recentSearchesV2', JSON.stringify(dataToSave));
    
    console.log('Recent searches saved to localStorage:', recentSearches.length, 'searches');
    
  } catch (error) {
    console.error('Failed to save recent searches to localStorage:', error);
  }
}

export default function* recentSearchesSaga() {
  yield takeLatest("recentSearches/loadRecentSearchesRequest", handleLoadRecentSearches);
  yield takeLatest("recentSearches/saveRecentSearchRequest", handleSaveRecentSearch);
  yield takeLatest("recentSearches/addRecentSearch", handleAddRecentSearch);
}

// Export individual functions for testing
export { handleLoadRecentSearches, handleSaveRecentSearch, handleAddRecentSearch };
